/**
 * Studios virtuels du studio « Monday Event » de Paris (demande de Daphné du 30/10). Fonctions pures.
 *
 * Sur Dynamo (5736526), RIISE (5736555) et Punch (5736551) Paris, la location Mindbody « Monday Event » (Id 10, 3 et 5)
 * porte à la fois les Run Clubs (programme 46 « RUN CLUB », Enrollment) et les événements (programme 42 « SPECIAL
 * EVENTS », Enrollment ; Maison Monday des 21-22/11/2026). Le planning v2 en fait deux entrées de studio : « Run Club »
 * (programme 46) et une entrée au libellé réglable (programme 42, réglage du BO « Nom de l'entrée Special Events du
 * studio Monday Event », UxV2::specialEventsLabel, injecté dans `uxV2.specialEventsLabel`). Les cours d'un autre
 * programme sur cette location restent sous « Monday Event ». La location n'est pas renommée dans Mindbody (cf. commit
 * d21bc21 : renommer « Monday Event » en « Run Club » avait étiqueté à tort l'événement RIISE 5 ANS, qui partage la
 * location ; la séparation se fait ici par programme, pas par location).
 *
 * Réglage vide : aucune séparation (une seule entrée « Monday Event », Run Club compris), comme avant.
 * Planning multi-marques : le libellé réglé sur le site qui AFFICHE le planning s'applique aux trois marques.
 *
 * Identifiant d'un studio virtuel : « {locationId}-{programId} » (« 10-46 », « 10-42 »), dans les clés « site:id » des
 * filtres et dans la route `studio/:id` (`#/studio/10-46`, `#/studio/10-42/week/47`). La v1 n'est pas modifiée.
 */
import { RUN_CLUB_PROGRAM_ID, RUN_CLUB_STUDIO_ID } from './status';

/** Programme Mindbody « SPECIAL EVENTS » des trois sites de Paris (Maison Monday, 21-22/11/2026). */
export const SPECIAL_EVENTS_PROGRAM_ID = 42;

/** Location « Monday Event » de chaque site de Paris (Id Mindbody propre à chaque site). */
export const EVENT_LOCATIONS: Readonly<Record<string, number>> = {
  '5736526': RUN_CLUB_STUDIO_ID,
  '5736555': 3,
  '5736551': 5,
};

/** Libellé de l'entrée du programme 46. */
export const RUN_CLUB_STUDIO_LABEL = 'Run Club';

/** Séparation active : libellé (non vide) de l'entrée du programme 42. null : pas de séparation. */
export interface EventSplit {
  specialLabel: string;
}

/** Réglage `uxV2.specialEventsLabel` → séparation, ou null s'il est vide ou absent. */
export function eventSplitOf(label: unknown): EventSplit | null {
  const clean = typeof label === 'string' ? label.replace(/\s+/g, ' ').trim() : '';
  return clean ? { specialLabel: clean } : null;
}

/** Séparation du site courant (window.mondayMindbody.uxV2.specialEventsLabel, UxV2::getClientConfig). */
export function readEventSplit(): EventSplit | null {
  const mm = typeof mondayMindbody !== 'undefined' && mondayMindbody ? mondayMindbody : undefined;
  return eventSplitOf((mm?.uxV2 as { specialEventsLabel?: unknown } | undefined)?.specialEventsLabel);
}

export interface EventClassLike {
  SiteId?: number | string | null;
  Location?: { Id?: number | string | null; SiteID?: number | string | null; Name?: string | null } | null;
  ClassDescription?: { Program?: { Id?: number | string | null } | null } | null;
}

const siteOfClass = (c: EventClassLike) => String(c.SiteId ?? c.Location?.SiteID ?? '');

/** Location « Monday Event » d'un site de Paris ? */
export const isEventLocation = (siteId: string, locationId: number | string | null | undefined) =>
  locationId !== null && locationId !== undefined && EVENT_LOCATIONS[siteId] === Number(locationId);

const SPLIT_PROGRAMS: ReadonlyArray<number> = [RUN_CLUB_PROGRAM_ID, SPECIAL_EVENTS_PROGRAM_ID];

/** Programme (46 ou 42) qui range ce cours dans un studio virtuel ; null : studio Mindbody d'origine. */
export function eventProgramOf(c: EventClassLike, split: EventSplit | null): number | null {
  if (!split || !isEventLocation(siteOfClass(c), c.Location?.Id)) return null;
  const program = Number(c.ClassDescription?.Program?.Id);
  return SPLIT_PROGRAMS.includes(program) ? program : null;
}

/** Identifiant de studio du cours : Location.Id, ou « {locationId}-{programId} » pour un studio virtuel. */
export function studioIdOf(c: EventClassLike, split: EventSplit | null): string {
  const program = eventProgramOf(c, split);
  const location = c.Location?.Id;
  return program === null ? String(location) : `${location}-${program}`;
}

/** Clé « site:id » du studio du cours (filtre Studios). */
export const studioKeyOf = (c: EventClassLike, split: EventSplit | null) =>
  `${siteOfClass(c)}:${studioIdOf(c, split)}`;

/** Libellé d'un programme séparé : « Run Club » (46), libellé du réglage (42). */
export const eventProgramLabel = (program: number, split: EventSplit) =>
  program === RUN_CLUB_PROGRAM_ID ? RUN_CLUB_STUDIO_LABEL : split.specialLabel;

/** Nom du studio virtuel du cours ; null si le cours reste dans son studio Mindbody. */
export function eventStudioName(c: EventClassLike, split: EventSplit | null): string | null {
  const program = eventProgramOf(c, split);
  return program === null || !split ? null : eventProgramLabel(program, split);
}

/** Nom du studio à afficher pour ce cours : studio virtuel, sinon Location.Name. */
export const locationNameOf = (c: EventClassLike, split: EventSplit | null): string =>
  eventStudioName(c, split) ?? c.Location?.Name ?? '';

/** Studios virtuels d'un site (Id « 10-46 », « 10-42 ») avec leur libellé ; [] sans séparation ou hors Paris. */
export function virtualStudiosOf(siteId: string, split: EventSplit | null): { id: string; label: string }[] {
  const location = EVENT_LOCATIONS[siteId];
  if (!split || location === undefined) return [];
  return SPLIT_PROGRAMS.map((program) => ({ id: `${location}-${program}`, label: eventProgramLabel(program, split) }));
}

/**
 * Clés « site:id » sélectionnées par la route `studio/:id` (Id du site courant) :
 * - `10-46` / `10-42` (location Monday Event + programme séparé) : l'entrée virtuelle si la séparation est active,
 *   sinon le studio 10 entier (un lien envoyé par un site réglé vers un site qui ne l'est pas reste valable) ;
 * - `10` avec séparation active : les trois entrées (Run Club, Special Events, Monday Event), pour que les liens
 *   existants `#/studio/10` montrent toujours tous les cours de la location ;
 * - tout autre Id « N-P » : le studio N.
 */
export function routeStudioKeys(siteId: string, rawId: string, split: EventSplit | null): string[] {
  const m = /^(\d+)(?:-(\d+))?$/.exec(rawId);
  if (!m) return [];
  const location = parseInt(m[1], 10);
  const program = m[2] ? parseInt(m[2], 10) : null;
  const key = (id: string | number) => `${siteId}:${id}`;
  if (!split || !isEventLocation(siteId, location)) return [key(location)];
  if (program === null) return [key(location), ...virtualStudiosOf(siteId, split).map((s) => key(s.id))];
  return SPLIT_PROGRAMS.includes(program) ? [key(`${location}-${program}`)] : [key(location)];
}

/** Studio du Run Club de Dynamo Paris dans la route du planning : « 10-46 » si la séparation est active, sinon « 10 ». */
export const runClubStudioRouteId = (split: EventSplit | null) =>
  split ? `${RUN_CLUB_STUDIO_ID}-${RUN_CLUB_PROGRAM_ID}` : String(RUN_CLUB_STUDIO_ID);
